import {
  Address,
  EingangsZahlweg,
  IBusinessData,
  ICustomerBankKonto,
  IDifferentInvoiceRecipient,
  IGeschaeftspartnerBeziehung,
  IOrganisation,
  IPartner,
  IPersonalData,
  IRechnungsempfaenger,
  IVertragskonto,
  Verrechnungskonto,
} from '@mwe/models';
import { getAccountCustomerId, getAccountOwner, getInvoiceAddress, getInvoiceCustomerId, getInvoiceEmail } from './mwe-util';
import { getAddressPostCodeAndStreetFormatted } from '../address/address.utils';

export const getPersonalDataFromClearingAccount = (clearingAccount: Verrechnungskonto, fallbackEmail?: string): IPersonalData => {
  const partnerDetail = getAccountOwner(clearingAccount.geschaeftspartnerBeziehungen);
  return getPersonalDataFromPartnerDetail(partnerDetail, fallbackEmail);
};

export const getOwnerContactFromPartnerDetail = (accountOwner: IGeschaeftspartnerBeziehung, address: Address): string => {
  if (!accountOwner || !address) {
    return '';
  }

  const formattedAddress = getAddressPostCodeAndStreetFormatted(address);
  const businessCompanyName = getBusinessCompanyNameFromIlSapData(accountOwner.organisation);
  if (businessCompanyName) {
    return `${businessCompanyName}: ${formattedAddress}`;
  }

  const personalData = getPersonalDataFromPartnerDetail(accountOwner);
  return `${personalData?.salutation} ${personalData?.firstName} ${personalData?.lastName}: ${formattedAddress}`;
};

export const getOwnerNameFromPartnerDetail = (partnerDetail: IGeschaeftspartnerBeziehung): string => {
  if (!partnerDetail) {
    return '';
  }

  const personalData = getPersonalDataFromPartnerDetail(partnerDetail);
  const businessCompanyName = getBusinessCompanyNameFromIlSapData(partnerDetail.organisation);
  return businessCompanyName ? businessCompanyName : `${personalData?.firstName} ${personalData?.lastName}`;
};

export const getPersonalDataFromPartnerDetail = (partnerDetail: IGeschaeftspartnerBeziehung, fallbackEmail?: string): IPersonalData => {
  // a B2B-user has {physischePerson: {vorname: ''}} - thank you SAP/IL...
  // check for firstName and lastName - some B2C users has only lastName...
  if (!partnerDetail?.physischePerson?.vorname && !partnerDetail?.physischePerson?.nachname) {
    return null;
  }

  const person = partnerDetail.physischePerson;

  return {
    salutation: person.anrede,
    title: { text: person.titelVorgestellt || person.titelNachgestellt, value: '' },
    firstName: person.vorname,
    lastName: person.nachname,
    birthday: person?.geburtsdatum ?? '',
    telefonNumber: partnerDetail?.telefon?.nummer,
    telefonCountryCode: partnerDetail?.telefon?.land,
    email: partnerDetail?.email?.eMail || fallbackEmail,
  };
};

export const getBusinessDataFromPartnerDetail = (partnerDetail: IGeschaeftspartnerBeziehung, fallbackEmail?: string): IBusinessData => {
  const business = partnerDetail?.organisation;
  // a B2C-user has {organisation: {organisationsname: '', organisationsname2: ''}} - thank you SAP/IL...
  if (!business?.organisationsname && !business?.organisationsname2) {
    return null;
  }

  return {
    type: 'BUSINESS',
    companyName: getBusinessCompanyNameFromIlSapData(business),
    corporateRegisterNumber: business.firmenbuchnummer,
    uidNumber: business.umsatzsteuerId,
    telefonNumber: partnerDetail?.telefon?.nummer,
    telefonCountryCode: partnerDetail?.telefon?.land,
    email: partnerDetail?.email?.eMail || fallbackEmail,
  };
};

export const getBusinessCompanyNameFromIlSapData = (business: IOrganisation): string => {
  // a B2C-user has {organisation: {organisationsname: '', organisationsname2: ''}} - thank you SAP/IL...
  if (!business?.organisationsname && !business?.organisationsname2) {
    return '';
  }

  // these should be handled by IL or SAP...
  if (business?.organisationsname && business?.organisationsname2) {
    return `${business.organisationsname} ${business.organisationsname2}`;
  }

  if (!business?.organisationsname && business?.organisationsname2) {
    return business.organisationsname2;
  }

  return business.organisationsname;
};

export const parseBusinessData = (orderDetails: any): IBusinessData => {
  return {
    type: 'BUSINESS',
    companyName: getBusinessCompanyNameFromIlSapData(orderDetails.organisation),
    corporateRegisterNumber: orderDetails?.organisation?.firmenbuchnummer,
    uidNumber: orderDetails?.organisation?.umsatzsteuerId,
    email: orderDetails?.personalData?.email,
    telefonNumber: orderDetails?.personalData?.telefonNumber,
    telefonCountryCode: orderDetails?.personalData?.telefonCountryCode,
  };
};

export const getFirstInvoiceMailFromClearingAccount = (details: Verrechnungskonto[]): string => {
  const emails = details.map(v => getInvoiceEmail(v));
  return emails.find(e => !!e);
};

export const getFirstSepaPaymentFromClearingAccount = (details: Verrechnungskonto[]): ICustomerBankKonto => {
  const vk = details.find(v => hasClearingAccountSepaMandant(v));
  if (vk) {
    // if sparte is stromrueck then the data is given by ausgangskonto
    return vk.eingangskonto ? vk.eingangskonto : vk.ausgangskonto;
  }
  return null;
};

export const hasClearingAccountDifferentInvoiceRecipient = (konto: Verrechnungskonto): boolean => {
  return getAccountCustomerId(konto?.geschaeftspartnerBeziehungen) !== getInvoiceCustomerId(konto?.geschaeftspartnerBeziehungen);
};

export const getNewMailingInfoForDifferentInvoiceRecipient = (details: Verrechnungskonto[]): IDifferentInvoiceRecipient => {
  const detail = details?.find(d => hasClearingAccountDifferentInvoiceRecipient(d));
  if (!detail) {
    return null;
  }

  const accountOwner = getAccountOwner(detail.geschaeftspartnerBeziehungen);
  return {
    invoiceAddress: getInvoiceAddress(detail.geschaeftspartnerBeziehungen),
    physischePerson: accountOwner.physischePerson,
    organisation: accountOwner.organisation,
  };
};

export const isClearingAccountPaidByZahlschein = (vertragskonto?: Verrechnungskonto): boolean => {
  return vertragskonto?.eingangsZahlweg === EingangsZahlweg.Zahlschein;
};

export const hasClearingAccountSepaMandant = (vertragskonto?: Verrechnungskonto): boolean => {
  return vertragskonto && vertragskonto.eingangsZahlweg === EingangsZahlweg.Sepa;
};

export const parseIPartnerFromClearingAccount = (clearingAccount: Verrechnungskonto): IPartner => {
  const accountCustomer = getAccountOwner(clearingAccount?.geschaeftspartnerBeziehungen);

  return {
    partnerId: accountCustomer?.geschaeftsPartnerId,
    partnerGruppe: null,
    nickname: null,
    registrierungStatus: null,
    rolle: null,
    partnerTyp: null,
    anrede: accountCustomer?.physischePerson.anrede,
    titel: accountCustomer?.physischePerson.titel,
    nachname: accountCustomer?.physischePerson.nachname,
    vorname: accountCustomer?.physischePerson.vorname,
    firma: null,
    email: accountCustomer?.email?.eMail,
    telefon: accountCustomer?.telefon?.nummer,
    telefonvorwahl: accountCustomer?.telefon?.land,
  };
};

export const parseIRechnungsempfaengerFromClearingAccount = (clearingAccount: Verrechnungskonto): IRechnungsempfaenger => {
  const accountCustomer = getAccountOwner(clearingAccount?.geschaeftspartnerBeziehungen);
  const invoiceAddress = getInvoiceAddress(clearingAccount?.geschaeftspartnerBeziehungen);
  return {
    partnerId: accountCustomer?.geschaeftsPartnerId,
    partnerGruppe: null,
    nickname: null,
    registrierungStatus: null,
    rolle: null,
    partnerTyp: null,
    anrede: accountCustomer?.physischePerson.anrede,
    titel: accountCustomer?.physischePerson.titel,
    nachname: accountCustomer?.physischePerson.nachname,
    vorname: accountCustomer?.physischePerson.vorname,
    firma: null,
    email: accountCustomer?.email?.eMail,
    telefon: accountCustomer?.telefon?.nummer,
    telefonvorwahl: accountCustomer?.telefon?.land,
    adresse: {
      ort: invoiceAddress?.ort,
      plz: invoiceAddress?.plz,
      strasse: invoiceAddress?.strasse,
      hausnummer: invoiceAddress?.hausnummer,
      land: invoiceAddress?.land,
      raum: invoiceAddress?.raumnummer,
      adresshinweis: invoiceAddress?.adresshinweis,
    },
  };
};

export const parseIVertragskontoFromClearingAccount = (customerVertragskonto: Verrechnungskonto): IVertragskonto => {
  return {
    vertragskontoId: customerVertragskonto?.verrechnungskontoId,
    zahlungsmethode: hasClearingAccountSepaMandant(customerVertragskonto) ? 'Bankeinzug' : 'Zahlschein',
    kontonummer: null,
    blz: null,
    iban: customerVertragskonto?.eingangskonto?.iban,
    bic: customerVertragskonto?.eingangskonto?.swift,
  };
};

export const areAllInvoicesSendByPost = (details: Verrechnungskonto[]): boolean => {
  return details.every(detail => !getInvoiceEmail(detail));
};

export const areAllInvoicesPaidByPaymentForm = (details: Verrechnungskonto[]): boolean => {
  return details.every(detail => isClearingAccountPaidByZahlschein(detail));
};

export const isOlderThan18Years = (birthDate: string): boolean => {
  const beforeEighteen = new Date();
  beforeEighteen.setFullYear(beforeEighteen.getFullYear() - 18);
  return beforeEighteen.getTime() > new Date(birthDate).getTime();
};
